/**
 * Phase 8 T-22 §27.B — Assistant intake inventory table.
 *
 * 13-row typed config that drives the single reusable `<AssistantIntake>`
 * component. Each row corresponds to one route in the app that surfaces the
 * signature "Hey {firstName}, …" intake pattern. No per-route forks — all
 * visual differences (greeting, placeholder, scope pill, 4 suggestion chips,
 * optional Beta badge) flow from this table.
 *
 * The §27.B **does-NOT-render allowlist** (/home, /dashboard, /settings/*,
 * /library, /clients) is enforced inside `useAssistantIntakeConfig`, not here.
 */

/** Entity kind the intake is authoring against. */
export type IntakeEntity =
  | 'candidate'
  | 'job'
  | 'requisition'
  | 'application'
  | 'email'
  | 'sequence'
  | 'interview'
  | 'workflow'
  | 'role-fit'
  | 'optimization'
  | 'bulk-action'
  | 'report';

/** Suggestion chip descriptor — icon key is resolved inside the component. */
export interface IntakeSuggestion {
  /** lucide-react icon name (lowercase). Rendered by the component. */
  icon: string;
  label: string;
  /**
   * Optional longer body that replaces `label` in the composer when the
   * chip is clicked. Used for JD-template chips on `/smart-match/new`
   * so picking "Senior Backend Engineer (Python)" drops the full
   * pre-authored JD into the textarea instead of just the role name.
   *
   * Kept optional so the 12 other intake routes — which use chips as
   * one-word filter shortcuts — don't need to change.
   */
  prompt?: string;
  /** Optional hint text surfaced as a tooltip (native `title`). */
  description?: string;
}

/** Scope pill — label + options. Optional per route. */
export interface IntakeScopePill {
  label: string;
  options: string[];
}

export interface IntakeConfig {
  /** Router path (supports params; the hook matches with `useMatch`). */
  route: string;
  entity: IntakeEntity;
  /** Plural label for streaming meta ("Evaluating candidates (3/10)…"). */
  entityPlural: string;
  /** Uses `{firstName}` token — replaced at render time. */
  greeting: string;
  placeholder: string;
  scopePill?: IntakeScopePill;
  /** Exactly ≤ 4 suggestion chips (component truncates if more). */
  suggestions: IntakeSuggestion[];
  /** Shows the `[🔍 Beta]` badge left of scope pill when true. */
  beta?: boolean;
}

/**
 * JD-template chips for the `/smart-match/new` intake.
 *
 * The `prompt` bodies intentionally duplicate the canonical definition
 * in `src/modules/smart-match/lib/jdTemplates.ts` — we keep the chip
 * data inline here so `intake.config.ts` stays a flat, dependency-free
 * registry (consumed by routes + tests). When you add / edit a
 * template, update both places. The `id` is stable for analytics.
 */
const JD_TEMPLATE_CHIPS: readonly IntakeSuggestion[] = [
  {
    icon: 'code-2',
    label: 'Senior Backend Engineer',
    description: 'Python + distributed systems, 5+ yrs',
    prompt:
      'Senior Backend Engineer — Python / Distributed Systems\n\n' +
      'We are hiring a Senior Backend Engineer with 5+ years of experience\n' +
      'building high-throughput services in Python. You will own services\n' +
      'that power our core platform — design, scale, debug, and harden.\n\n' +
      'Must-have\n' +
      '- 5+ years production Python (FastAPI, Django, or similar)\n' +
      '- Strong grasp of PostgreSQL + Redis + async processing\n' +
      '- Hands-on with AWS (ECS / Lambda / SQS) or GCP equivalents\n' +
      '- Experience running services at >1k RPS in production\n\n' +
      'Nice-to-have\n' +
      '- Go or Rust for hot paths\n' +
      '- OpenTelemetry / distributed tracing\n' +
      '- Previous staff-level tech lead experience\n\n' +
      'Location: Remote (India / EU timezones). Budget: competitive.',
  },
  {
    icon: 'figma',
    label: 'Senior Product Designer',
    description: 'Figma, design systems, B2B SaaS, 5+ yrs',
    prompt:
      'Senior Product Designer — B2B SaaS\n\n' +
      'Looking for a Senior Product Designer (5+ yrs) who has shipped\n' +
      'B2B SaaS products end-to-end. You will own the design for one of\n' +
      'our core recruiter workflows, from discovery to handoff.\n\n' +
      'Must-have\n' +
      '- 5+ years product design, at least 2 in B2B SaaS\n' +
      '- Expert in Figma, component libraries, and design systems\n' +
      '- Strong systems-thinking — information architecture, flows,\n' +
      '  empty / error / loading states\n' +
      '- Comfortable partnering with PMs & engineers on tight timelines\n\n' +
      'Nice-to-have\n' +
      '- Prior AI / ML product surface experience\n' +
      '- Accessibility (WCAG AA) fluency\n' +
      '- Past work in HR-tech or recruiting\n\n' +
      'Location: Remote-first, India + US overlap. Budget: competitive.',
  },
  {
    icon: 'flask-conical',
    label: 'Data Scientist / ML',
    description: 'NLP + LLM evaluation, 3+ yrs, Python + PyTorch',
    prompt:
      'Data Scientist / ML Engineer — NLP & LLM Evaluation\n\n' +
      'We are hiring a Data Scientist with 3+ years of applied ML\n' +
      'experience, ideally working on NLP or LLM evaluation pipelines.\n' +
      'You will own model quality for our recruiter-facing ranking and\n' +
      're-ranking layer.\n\n' +
      'Must-have\n' +
      '- 3+ years applied ML (Python, PyTorch or JAX)\n' +
      '- Hands-on with transformer models, embeddings, retrieval\n' +
      '- Experience designing offline evaluation sets and A/B tests\n' +
      '- Comfort writing production-quality code, not just notebooks\n\n' +
      'Nice-to-have\n' +
      '- Vector databases (Pinecone, Weaviate, pgvector)\n' +
      '- LangChain / LlamaIndex or similar orchestration frameworks\n' +
      '- Published or open-sourced ML work\n\n' +
      'Location: Hybrid (Bengaluru / Remote). Budget: competitive.',
  },
  {
    icon: 'megaphone',
    label: 'B2B Marketing Lead',
    description: 'Demand-gen + content, SaaS, 6+ yrs',
    prompt:
      'B2B Marketing Lead — Demand Generation & Content\n\n' +
      'We are hiring a hands-on Marketing Lead (6+ yrs) to own demand\n' +
      'generation for our B2B recruiter product. You will ship campaigns,\n' +
      'manage a small team, and partner with sales on pipeline.\n\n' +
      'Must-have\n' +
      '- 6+ years B2B SaaS marketing, 2+ in a lead role\n' +
      '- Track record running full-funnel demand-gen (paid + organic)\n' +
      '- Strong content instincts — landing pages, sequences, webinars\n' +
      '- Comfortable with analytics stacks (GA4, HubSpot, Clearbit)\n\n' +
      'Nice-to-have\n' +
      '- Experience in HR-tech, recruiting, or talent marketplaces\n' +
      '- Early-stage or 0 → 1 launch experience\n' +
      '- ABM playbook under your belt\n\n' +
      'Location: Remote-first, India + US overlap. Budget: competitive.',
  },
] as const;

/**
 * §27.B — the canonical table. Order matches the doc for review parity.
 * Keep this list exactly 13 rows; the acceptance test iterates it.
 */
export const INTAKE_CONFIGS: readonly IntakeConfig[] = [
  {
    route: '/smart-match/new',
    entity: 'candidate',
    entityPlural: 'candidates',
    greeting: 'Hey {firstName}, what can I help you find?',
    placeholder: 'Paste a JD, describe an ideal candidate, or pick a template below…',
    scopePill: {
      label: 'Sourcing over',
      options: ['Org database', 'Career page', 'External'],
    },
    // 21-Apr follow-up: chips are JD templates (not role names). The
    // full template body lives in `src/modules/smart-match/lib/jdTemplates.ts`
    // — this array mirrors those templates so a chip click drops the
    // complete JD into the composer via the optional `prompt` field.
    // Keep these four in sync with `JD_TEMPLATES` ordering.
    suggestions: JD_TEMPLATE_CHIPS,
    beta: true,
  },
  {
    route: '/candidates',
    entity: 'candidate',
    entityPlural: 'candidates',
    greeting: 'Hey {firstName}, who are you looking for?',
    placeholder: 'Describe the candidate, role, or context…',
    scopePill: {
      label: 'Sourcing over',
      options: ['Org database', 'Career page', 'External'],
    },
    suggestions: [
      { icon: 'star', label: 'Recent placements' },
      { icon: 'briefcase', label: 'Open roles' },
      { icon: 'list-checks', label: 'My shortlists' },
      { icon: 'calendar-check', label: 'Past interviews' },
    ],
  },
  {
    route: '/jobs/new',
    entity: 'job',
    entityPlural: 'jobs',
    greeting: 'Hey {firstName}, what role are you opening?',
    placeholder: 'Describe the role, seniority, location, must-haves…',
    scopePill: {
      label: 'Input type',
      options: ['Quick JD', 'Detailed JD', 'Import URL'],
    },
    suggestions: [
      { icon: 'briefcase', label: 'Senior PM' },
      { icon: 'code-2', label: 'Staff SWE' },
      { icon: 'figma', label: 'Design Lead' },
      { icon: 'handshake', label: 'Account Executive' },
    ],
  },
  {
    route: '/requisitions/new',
    entity: 'requisition',
    entityPlural: 'requisitions',
    greeting: 'Hey {firstName}, what\'s the business case?',
    placeholder: 'Headcount, client, budget, deadline, dependencies…',
    scopePill: {
      label: 'Hire type',
      options: ['Internal hire', 'Client hire', 'Replacement'],
    },
    suggestions: [
      { icon: 'alert-triangle', label: 'Urgent fill' },
      { icon: 'users', label: 'New team' },
      { icon: 'repeat', label: 'Replace attrition' },
      { icon: 'arrow-right-left', label: 'Contract → FTE' },
    ],
  },
  {
    route: '/applications',
    entity: 'application',
    entityPlural: 'applications',
    greeting: 'Hey {firstName}, what should I surface today?',
    placeholder: 'Filter applications by role, stage, score, urgency…',
    scopePill: {
      label: 'Scope',
      options: ['All apps', 'My pipelines', 'Client pipelines'],
    },
    suggestions: [
      { icon: 'trending-up', label: 'High scores' },
      { icon: 'clock', label: 'Stuck ≥ 3 days' },
      { icon: 'mail', label: 'Replies pending' },
      { icon: 'calendar-check', label: 'Interview-ready' },
    ],
  },
  {
    route: '/emails/compose',
    entity: 'email',
    entityPlural: 'emails',
    greeting: 'Hey {firstName}, what do you want to say?',
    placeholder: "Recipient + goal + tone. I'll draft it.",
    scopePill: {
      label: 'Send mode',
      options: ['Single', 'Bulk (≥ 2 recipients)'],
    },
    suggestions: [
      { icon: 'handshake', label: 'Intro reach-out' },
      { icon: 'calendar', label: 'Interview invite' },
      { icon: 'gift', label: 'Offer' },
      { icon: 'reply', label: 'Rejection follow-up' },
    ],
  },
  {
    route: '/sequences/new',
    entity: 'sequence',
    entityPlural: 'sequences',
    greeting: 'Hey {firstName}, what\'s the journey?',
    placeholder: 'Describe the cadence and goal…',
    scopePill: {
      label: 'Audience',
      options: ['Candidate', 'Client', 'Passive talent'],
    },
    suggestions: [
      { icon: 'layers', label: '3-step nurture' },
      { icon: 'snowflake', label: 'Re-engage cold' },
      { icon: 'clipboard-check', label: 'Post-interview check-in' },
      { icon: 'infinity', label: 'Long-term pool' },
    ],
  },
  {
    route: '/interviews/new',
    entity: 'interview',
    entityPlural: 'interviews',
    greeting: 'Hey {firstName}, who are we meeting?',
    placeholder: 'Candidate, role, stage, availability…',
    scopePill: {
      label: 'Type',
      options: ['Phone screen', 'Panel', 'Final', 'AI-screen'],
    },
    suggestions: [
      { icon: 'phone', label: '30 min screen' },
      { icon: 'users', label: '60 min panel' },
      { icon: 'file-text', label: 'Take-home review' },
      { icon: 'phone-call', label: 'Reference call' },
    ],
  },
  {
    route: '/workflows/new',
    entity: 'workflow',
    entityPlural: 'workflows',
    greeting: 'Hey {firstName}, what should I automate?',
    placeholder: 'Describe the trigger, conditions, and action…',
    scopePill: {
      label: 'Scope',
      options: ['Pipeline', 'Client', 'Org'],
    },
    suggestions: [
      { icon: 'x-circle', label: 'Auto-reject on score < 60' },
      { icon: 'arrow-right', label: 'Move to screen on apply' },
      { icon: 'bell', label: 'Notify on stall' },
      { icon: 'mail', label: 'Chase no-reply' },
    ],
  },
  {
    route: '/role-fit/new',
    entity: 'role-fit',
    entityPlural: 'matches',
    greeting: 'Hey {firstName}, which role are we fitting for?',
    placeholder: 'Paste the JD or describe the role…',
    // No scope pill per §27.B
    suggestions: [
      { icon: 'clipboard', label: 'Paste JD' },
      { icon: 'briefcase', label: 'Use open req' },
      { icon: 'file-text', label: 'Use template' },
    ],
  },
  {
    route: '/single-optimize/new',
    entity: 'optimization',
    entityPlural: 'optimizations',
    greeting: 'Hey {firstName}, what outcome do you want?',
    placeholder: 'Describe the goal and the current state…',
    // No scope pill per §27.B
    suggestions: [
      { icon: 'clock', label: 'Shorten time-to-hire' },
      { icon: 'trending-up', label: 'Lift pass rate' },
      { icon: 'scale', label: 'Balance gender ratio' },
      { icon: 'piggy-bank', label: 'Reduce cost/hire' },
    ],
  },
  {
    route: '/bulk/:action',
    entity: 'bulk-action',
    entityPlural: 'actions',
    greeting: 'Hey {firstName}, what do you want to do with {N} selected?',
    placeholder: 'Describe the action in plain language…',
    scopePill: {
      label: 'Target',
      options: ['Candidates', 'Applications', 'Jobs'],
    },
    suggestions: [
      { icon: 'send', label: 'Send outreach' },
      { icon: 'x-circle', label: 'Reject & notify' },
      { icon: 'user-plus', label: 'Assign owner' },
      { icon: 'arrow-right', label: 'Move to stage' },
    ],
  },
  {
    route: '/reports/ask',
    entity: 'report',
    entityPlural: 'reports',
    greeting: 'Hey {firstName}, what do you want to measure?',
    placeholder: 'Ask about pipeline health, SLAs, conversion, outcomes…',
    scopePill: {
      label: 'Scope',
      options: ['Org', 'Client', 'Team'],
    },
    suggestions: [
      { icon: 'activity', label: 'Pipeline velocity' },
      { icon: 'check-circle', label: 'Offer acceptance' },
      { icon: 'clock', label: 'Stuck candidates' },
      { icon: 'calendar', label: 'Weekly summary' },
    ],
  },
] as const;

/**
 * §27.B allowlist — routes that must NEVER render the intake even if a
 * collision with a configured route occurs. Matched via prefix, so
 * `/settings/*` is expressed as `/settings`.
 */
export const INTAKE_DO_NOT_RENDER_ROUTES: readonly string[] = [
  '/home',
  '/dashboard',
  '/settings',
  '/library',
  '/clients',
] as const;
